import { DateTime } from '@ksyos/date-time';
import {
    Badge,
    Box,
    DatePicker,
    IconButton,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { orderBy } from 'lodash';
import { Fragment } from 'react';
import type {
    IPractitioner,
    IServiceGroupSetting,
    IServiceOrganizationGroupHistory,
    IServiceOrganizationSettings,
} from '@frontend/components/interfaces';
import type { ValidationErrors } from '@frontend/utils/validation';
import { getLanguage } from '@common/translate/translate';
import { getDisplayName } from '@common/utils/getDisplayName';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { findAndSetCurrentServiceGroup } from '../utils/serviceOrganizationGroupHistoryUtils';

interface IGroupHistoryTableEditProps {
    columns: string[];
    errors: ValidationErrors;
    handleChange: (
        key: keyof IServiceOrganizationSettings,
        value: IServiceOrganizationSettings[keyof IServiceOrganizationSettings]
    ) => void;
    practitioner: IPractitioner;
    serviceGroupSettingOptions: IServiceGroupSetting[];
    serviceOrganizationGroupHistory: IServiceOrganizationGroupHistory[];
    setCurrentServiceGroup: (currentServiceGroup: IServiceGroupSetting | null) => void;
    validateOne: (key: keyof IServiceOrganizationSettings) => void;
}

export function GroupHistoryTableEdit(props: IGroupHistoryTableEditProps) {
    const {
        serviceOrganizationGroupHistory,
        columns,
        errors,
        validateOne,
        handleChange,
        serviceGroupSettingOptions,
        setCurrentServiceGroup,
        practitioner,
    } = props;
    const filteredServiceOrganizationGroupHistory = orderBy(
        serviceOrganizationGroupHistory.filter((sogh) => !sogh.isDeleted) || []
    );
    const translate = getLanguage();

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map((header) => (
                            <TableHeader
                                key={`edit-${header}`}
                                sx={{ minWidth: header === columns[0] ? '250px' : 'auto' }}
                            >
                                {header}
                            </TableHeader>
                        ))}
                        <TableHeader></TableHeader>
                        <TableHeader></TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {filteredServiceOrganizationGroupHistory.map((serviceOrganizationGroup) => {
                        const index = serviceOrganizationGroupHistory.findIndex(
                            (sOGH) => sOGH.id === serviceOrganizationGroup.id
                        );

                        return (
                            <Fragment key={`edit-${serviceOrganizationGroup.id}`}>
                                <TableRow>
                                    <TableCell>
                                        {serviceOrganizationGroup.isNew ? (
                                            <Select
                                                error={errors.get(
                                                    `serviceOrganizationGroupHistory.${index}.serviceGroupSettingId`
                                                )}
                                                menuPortalTarget={document.body}
                                                onBlur={() => validateOne('serviceOrganizationGroupHistory')}
                                                onChange={(option) => {
                                                    handleChangeServiceGroupSettingId(
                                                        option?.value ?? null,
                                                        serviceOrganizationGroup.id
                                                    );
                                                }}
                                                options={getOptions()}
                                                placeholder="Selecteer een groep"
                                                size="medium"
                                                value={
                                                    serviceOrganizationGroup.serviceGroupSetting?.group.id
                                                        ? {
                                                              label: serviceOrganizationGroup.serviceGroupSetting.group
                                                                  .name,
                                                              value: serviceOrganizationGroup.serviceGroupSetting.group
                                                                  .id,
                                                          }
                                                        : null
                                                }
                                            />
                                        ) : (
                                            serviceOrganizationGroup.serviceGroupSetting?.group.name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {serviceOrganizationGroup.lastUpdatedByPractitioner
                                            ? getDisplayName(
                                                  serviceOrganizationGroup.lastUpdatedByPractitioner,
                                                  false,
                                                  'nameGivenNameFamily'
                                              )
                                            : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            display="grid"
                                            gap={4}
                                            gridTemplateColumns="max-content max-content max-content"
                                        >
                                            <Box>
                                                <DatePicker
                                                    error={getValidationErrorTranslation(
                                                        errors.get(`serviceOrganizationGroupHistory.${index}.startDate`)
                                                    )}
                                                    onBlur={() => validateOne('serviceOrganizationGroupHistory')}
                                                    onChange={(value) => {
                                                        handleChangeGroups(
                                                            value ? DateTime.from(value).format('YYYY-MM-DD') : null,
                                                            'startDate',
                                                            serviceOrganizationGroup.id
                                                        );
                                                    }}
                                                    size="medium"
                                                    value={getDateValue(serviceOrganizationGroup.startDate)}
                                                />
                                            </Box>
                                            <Text
                                                as="span"
                                                mt={2}
                                                size="sm"
                                            >
                                                {translate.common.until}
                                            </Text>
                                            <Box>
                                                <DatePicker
                                                    error={getValidationErrorTranslation(
                                                        errors.get(`serviceOrganizationGroupHistory.${index}.endDate`)
                                                    )}
                                                    onBlur={() => validateOne('serviceOrganizationGroupHistory')}
                                                    onChange={(value) =>
                                                        handleChangeGroups(
                                                            value ? DateTime.from(value).format('YYYY-MM-DD') : null,
                                                            'endDate',
                                                            serviceOrganizationGroup.id
                                                        )
                                                    }
                                                    size="medium"
                                                    value={getDateValue(serviceOrganizationGroup.endDate)}
                                                />
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            icon="delete"
                                            onClick={() =>
                                                handleChangeGroups(true, 'isDeleted', serviceOrganizationGroup.id)
                                            }
                                            size="medium"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {!!serviceOrganizationGroup.startDate &&
                                            isActiveOnDate(serviceOrganizationGroup, new Date()) && (
                                                <Badge
                                                    color="green"
                                                    size="small"
                                                    text="Huidige"
                                                />
                                            )}
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function getOptions() {
        return serviceGroupSettingOptions.map((option) => ({
            label: option.group.name,
            value: option.group.id,
        }));
    }

    async function handleChangeGroups<T extends keyof IServiceOrganizationGroupHistory>(
        value: IServiceOrganizationGroupHistory[T],
        key: T,
        id: string
    ) {
        const updatedServiceOrganizationGroupHistory = updateServiceOrganizationGroupHistory(value, key, id);
        await findAndSetCurrentServiceGroup(updatedServiceOrganizationGroupHistory, setCurrentServiceGroup);
        await handleChange('serviceOrganizationGroupHistory', updatedServiceOrganizationGroupHistory);
    }

    async function handleChangeServiceGroupSettingId(value: string | null, serviceOrganizationGroupId: string) {
        const serviceGroupSetting = serviceGroupSettingOptions.find((serviceGroup) => serviceGroup.group.id === value);
        await handleChangeGroups(serviceGroupSetting, 'serviceGroupSetting', serviceOrganizationGroupId);
    }

    function getDateValue(date: string | null) {
        return date ? DateTime.from(date).toDate() : null;
    }

    function updateServiceOrganizationGroupHistory<T extends keyof IServiceOrganizationGroupHistory>(
        value: IServiceOrganizationGroupHistory[T],
        key: T,
        id: string
    ) {
        return serviceOrganizationGroupHistory
            .map((serviceGroupHistory) => {
                const updatedServiceGroup = { ...serviceGroupHistory };
                if (serviceGroupHistory.id === id) {
                    if (key === 'serviceGroupSetting') {
                        updatedServiceGroup.serviceGroupSettingId =
                            value && typeof value === 'object' ? value.id : null;
                    }
                    updatedServiceGroup[key] = value;
                    updatedServiceGroup.lastUpdatedByPractitionerId = practitioner.id;
                }

                return updatedServiceGroup;
            })
            .filter((serviceGroupHistory) => !(serviceGroupHistory.isDeleted && serviceGroupHistory.isNew));
    }
}
